/**
 * Marketplace Orders List Page
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  ShoppingCart as OrdersIcon,
  TrendingUp as TrendingUpIcon,
  Payments as RevenueIcon,
  AttachMoney as AvgIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, Column, PermissionGuard, StatsCard } from '../../components/common';
import {
  useGetMarketplaceOrdersQuery,
  useUpdateMarketplaceOrderStatusMutation,
  useGetMarketplaceOrderStatsQuery,
} from '../../store/api';
import { MarketplaceOrder, MarketplaceOrderStatus } from '../../types/marketplace';
import { formatCurrency } from '../../utils/pricing';
import moment from 'moment';

const ORDER_STATUS_CONFIG: Record<
  MarketplaceOrderStatus,
  { label: string; color: 'default' | 'info' | 'primary' | 'success' | 'error' | 'warning' | 'secondary' }
> = {
  PENDING: { label: 'Pending', color: 'warning' },
  CONFIRMED: { label: 'Confirmed', color: 'info' },
  PROCESSING: { label: 'Processing', color: 'primary' },
  OUT_FOR_DELIVERY: { label: 'Out for Delivery', color: 'secondary' },
  DELIVERED: { label: 'Delivered', color: 'success' },
  CANCELLED: { label: 'Cancelled', color: 'error' },
  RETURNED: { label: 'Returned', color: 'default' },
  REFUNDED: { label: 'Refunded', color: 'default' },
};

// Real product-order lifecycle — must mirror
// apps/dashboard/services/marketplace_management.get_allowed_marketplace_status_targets.
// Forward-only fulfilment; no approval gate.
const MARKETPLACE_FLOW_RANK: Record<string, number> = {
  PENDING: 0,
  CONFIRMED: 1,
  PROCESSING: 2,
  OUT_FOR_DELIVERY: 3,
  DELIVERED: 4,
};

const PAYMENT_STATUS_COLOR: Record<
  string,
  'default' | 'info' | 'primary' | 'success' | 'error' | 'warning'
> = {
  PENDING: 'warning',
  PROCESSING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
  REFUNDED: 'default',
  PARTIALLY_REFUNDED: 'default',
};

const getAllowedMarketplaceStatusTargets = (
  current: MarketplaceOrderStatus
): MarketplaceOrderStatus[] => {
  if (current === 'REFUNDED') return [];
  if (current === 'CANCELLED' || current === 'RETURNED') return ['REFUNDED'];
  if (current === 'DELIVERED') return ['RETURNED', 'REFUNDED'];
  const rank = MARKETPLACE_FLOW_RANK[current];
  const forward = (Object.keys(MARKETPLACE_FLOW_RANK) as MarketplaceOrderStatus[]).filter(
    (s) => MARKETPLACE_FLOW_RANK[s] > rank
  );
  return [...forward, 'CANCELLED'];
};

export const MarketplaceOrdersListPage = ({ embedded = false }: { embedded?: boolean } = {}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const statusLabel = (s: MarketplaceOrderStatus) => t(`orders.status.${s}`);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('');

  const { data, isLoading, error } = useGetMarketplaceOrdersQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    status: status || undefined,
  });

  const { data: statsResp } = useGetMarketplaceOrderStatsQuery();
  const stats = statsResp?.data;

  const [updateStatus, { isLoading: isUpdating }] = useUpdateMarketplaceOrderStatusMutation();
  const [editOrder, setEditOrder] = useState<MarketplaceOrder | null>(null);
  const [newStatus, setNewStatus] = useState<MarketplaceOrderStatus | ''>('');
  const [adminNotes, setAdminNotes] = useState('');

  const openStatusDialog = (order: MarketplaceOrder) => {
    setEditOrder(order);
    setNewStatus(''); // must pick a valid next status; current is not a target
    setAdminNotes('');
  };

  const allowedStatuses = editOrder
    ? getAllowedMarketplaceStatusTargets(editOrder.status)
    : [];

  const closeStatusDialog = () => {
    setEditOrder(null);
    setNewStatus('');
    setAdminNotes('');
  };

  const handleUpdateStatus = async () => {
    if (!editOrder || !newStatus) return;
    try {
      await updateStatus({
        id: editOrder.id,
        status: newStatus,
        ...(adminNotes.trim() && { admin_notes: adminNotes.trim() }),
      }).unwrap();
      toast.success(t('orders.toast.updated'));
      closeStatusDialog();
    } catch (err: any) {
      const fieldErr = err?.data?.errors?.status;
      const msg = Array.isArray(fieldErr) ? fieldErr[0] : fieldErr;
      toast.error(msg || err?.data?.message || t('orders.toast.updateFailed'));
    }
  };

  const columns: Column<MarketplaceOrder>[] = [
    {
      id: 'order_number',
      label: t('orders.cols.orderNumber'),
      minWidth: 160,
      render: (row) => (
        <Box
          onClick={() => navigate(`/marketplace/orders/${row.id}`)}
          sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          {row.order_number}
        </Box>
      ),
    },
    {
      id: 'user_name',
      label: t('orders.cols.customer'),
      minWidth: 180,
      render: (row) => row.user_name || row.user_email,
    },
    {
      id: 'status',
      label: t('orders.cols.status'),
      minWidth: 140,
      render: (row) => (
        <Chip label={statusLabel(row.status)} size="small" color={ORDER_STATUS_CONFIG[row.status].color} />
      ),
    },
    {
      id: 'total_amount',
      label: t('orders.cols.total'),
      minWidth: 110,
      align: 'right',
      render: (row) => formatCurrency(row.total_amount),
    },
    {
      id: 'items_count',
      label: t('orders.cols.items'),
      minWidth: 80,
      align: 'center',
      render: (row) => <Chip label={row.items_count ?? row.items?.length ?? 0} size="small" />,
    },
    {
      id: 'payment',
      label: t('orders.cols.payment'),
      minWidth: 150,
      render: (row) =>
        row.payment_status ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Chip
              label={t(`payments.status.${row.payment_status}`, {
                defaultValue: row.payment_status,
              })}
              size="small"
              color={PAYMENT_STATUS_COLOR[row.payment_status] ?? 'default'}
            />
            {row.payment_method && (
              <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
                {t(`payments.method.${row.payment_method}`, {
                  defaultValue: row.payment_method,
                })}
              </Box>
            )}
          </Box>
        ) : (
          <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
            —
          </Box>
        ),
    },
    {
      id: 'created_at',
      label: t('orders.cols.created'),
      minWidth: 120,
      render: (row) => moment(row.created_at).format('MMM DD, YYYY'),
    },
    {
      id: 'actions',
      label: t('orders.cols.actions'),
      minWidth: 90,
      align: 'center',
      render: (row) => (
        <PermissionGuard permissions={['manage_marketplace']}>
          <Tooltip title={t('orders.changeStatus')}>
            <IconButton
              size="small"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                openStatusDialog(row);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </PermissionGuard>
      ),
    },
  ];

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded && (
        <PageHeader
          title={`${t('nav.marketplace')} — ${t('orders.title')}`}
          breadcrumbs={[
            { label: t('nav.dashboard'), path: '/' },
            { label: t('nav.marketplace'), path: '/marketplace/categories' },
            { label: t('orders.title') },
          ]}
        />
      )}

      {/* Statistics */}
      {stats && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2,
            mb: 3,
          }}
        >
          <StatsCard title={t('orders.stats.totalOrders')} value={stats.overview.total_orders} icon={<OrdersIcon />} color="primary" />
          <StatsCard title={t('orders.stats.ordersToday')} value={stats.overview.orders_today} icon={<TrendingUpIcon />} color="info" />
          <StatsCard title={t('orders.stats.totalRevenue')} value={formatCurrency(stats.revenue.total_revenue)} icon={<RevenueIcon />} color="success" />
          <StatsCard title={t('orders.stats.avgOrderValue')} value={formatCurrency(stats.revenue.average_order_value)} icon={<AvgIcon />} color="secondary" />
        </Box>
      )}

      {/* Filters */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder={t('orders.filters.searchPlaceholder')}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('orders.filters.status')}</InputLabel>
            <Select
              value={status}
              label={t('orders.filters.status')}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">{t('orders.filters.allStatuses')}</MenuItem>
              {Object.keys(ORDER_STATUS_CONFIG).map((key) => (
                <MenuItem key={key} value={key}>
                  {statusLabel(key as MarketplaceOrderStatus)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.results || []}
        loading={isLoading}
        error={error ? t('common.error') : null}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={data?.count || 0}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        emptyMessage={t('orders.empty')}
        onRowClick={(row) => navigate(`/marketplace/orders/${row.id}`)}
      />

      {/* Change status dialog */}
      <Dialog open={Boolean(editOrder)} onClose={closeStatusDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('orders.dialog.title')}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editOrder && (
            <Box sx={{ mb: 2, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
              {t('orders.dialog.order')} <strong>{editOrder.order_number}</strong> — {t('orders.dialog.current')}:
              <Chip
                label={statusLabel(editOrder.status)}
                size="small"
                color={ORDER_STATUS_CONFIG[editOrder.status].color}
              />
            </Box>
          )}
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }} disabled={allowedStatuses.length === 0}>
            <InputLabel>{t('orders.dialog.newStatus')}</InputLabel>
            <Select
              value={newStatus}
              label={t('orders.dialog.newStatus')}
              onChange={(e) => setNewStatus(e.target.value as MarketplaceOrderStatus)}
            >
              {allowedStatuses.map((key) => (
                <MenuItem key={key} value={key}>
                  {statusLabel(key)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {allowedStatuses.length === 0 && (
            <Box sx={{ mb: 2, color: 'text.secondary', fontSize: 14 }}>
              {t('orders.dialog.finalState')}
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('orders.dialog.adminNotes')}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusDialog}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleUpdateStatus}
            disabled={!newStatus || isUpdating || newStatus === editOrder?.status}
          >
            {isUpdating ? t('common.loading') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarketplaceOrdersListPage;
